import Polyfill from "../Polyfill";
import {CartItemRequest, CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {QuantityControl} from "./QuantityControl";
import {CartFeedback} from "./CartFeedback";

/**
 * Brings the format dialog for several images to life.
 *
 * Nothing is sent while the quantities are changed. "Add to cart" builds
 * one request with every image times every format with a quantity and
 * sends it in one go; afterwards the dialog shows the success message with
 * the buttons to go on shopping or to the cart.
 */
export class MultiAdd2CartDialogContent {
    static CONTAINER_SELECTOR = '.imagetypeselection-multi'
    static ADD_BUTTON_SELECTOR = '.eventgallery-add-selection'
    static CART_BUTTON_SELECTOR = '.eventgallery-opencart'
    static SELECT_STATE_SELECTOR = '.eg-multi-actions-select'
    static DONE_STATE_SELECTOR = '.eg-multi-actions-done'
    static SUCCESS_SELECTOR = '.eventgallery-multi-success'
    static PARTIAL_SELECTOR = '.eventgallery-multi-partial-error'
    static ERROR_SELECTOR = '.eventgallery-cart-error'
    static FORMATS_SELECTOR = '.imagetypeselection'

    cartProcessor: CartProcessor;
    container: HTMLElement;
    items: {folder: string, file: string}[] = [];
    quantityControl: QuantityControl;

    addButton: HTMLButtonElement;
    cartButton: HTMLButtonElement;

    private isSending: boolean = false;
    private failed: boolean = false;
    private pendingHref: string | null = null;

    constructor(cartProcessor: CartProcessor, root: ParentNode) {
        this.cartProcessor = cartProcessor;
        this.container = root.querySelector(MultiAdd2CartDialogContent.CONTAINER_SELECTOR);
        if (!this.container) {
            return;
        }

        try {
            this.items = JSON.parse(this.container.getAttribute('data-items') || '[]');
        } catch (e) {
            this.items = [];
        }

        this.addButton = this.container.querySelector(MultiAdd2CartDialogContent.ADD_BUTTON_SELECTOR);
        this.cartButton = this.container.querySelector(MultiAdd2CartDialogContent.CART_BUTTON_SELECTOR);

        this.quantityControl = new QuantityControl(this.container, {
            debounceMs: 0,
            onChange: () => this.refresh(),
            onCommit: () => this.refresh()
        });

        if (this.addButton) {
            this.addButton.addEventListener('click', (e) => this.addSelection(e));
        }
        if (this.cartButton) {
            this.cartButton.addEventListener('click', (e) => this.openCart(e));
        }

        document.addEventListener(Events.error, () => { this.failed = true; });
        document.addEventListener(Events.reload, (e: CustomEvent) => CartFeedback.update(this.container, e.detail));
        document.addEventListener(Events.busy, (e: CustomEvent) => {
            if (e.detail?.busy === false && this.pendingHref) {
                const href = this.pendingHref;
                this.pendingHref = null;
                document.location.href = href;
            }
        });

        CartFeedback.update(this.container, this.cartProcessor.getLastCartData());
        this.refresh();
    }

    /**
     * Number of items the request would add: every quantity applies to
     * every image.
     */
    totalCount(): number {
        let perImage = 0;
        this.quantityControl.getInputs().forEach((input) => {
            perImage += this.quantityControl.getValue(input);
        });
        return perImage * this.items.length;
    }

    refresh() {
        if (!this.addButton) {
            return;
        }
        const total = this.totalCount();
        this.addButton.disabled = total === 0 || this.isSending;

        const template = this.addButton.getAttribute('data-template') || '';
        if (total > 0 && template) {
            this.addButton.textContent = template.replace('{count}', String(total));
        }
    }

    buildRequests(): CartItemRequest[] {
        const requests: CartItemRequest[] = [];
        this.quantityControl.getInputs().forEach((input) => {
            const quantity = this.quantityControl.getValue(input);
            const imagetypeid = parseInt(input.getAttribute('data-imagetypeid') || '0');
            if (input.disabled || quantity <= 0 || !imagetypeid) {
                return;
            }
            this.items.forEach((item) => {
                requests.push({folder: item.folder, file: item.file, imagetypeid: imagetypeid, quantity: quantity});
            });
        });
        return requests;
    }

    addSelection(e: Event) {
        e.preventDefault();
        const requests = this.buildRequests();
        if (requests.length === 0 || this.isSending) {
            return;
        }

        this.isSending = true;
        this.failed = false;
        this.hide(MultiAdd2CartDialogContent.ERROR_SELECTOR);
        this.refresh();

        this.cartProcessor.addItems(requests).then(() => {
            this.isSending = false;
            if (this.failed) {
                this.show(MultiAdd2CartDialogContent.ERROR_SELECTOR);
                this.refresh();
                return;
            }
            this.showSuccess();
        });
    }

    /**
     * The server may have turned some images down, e.g. one which was
     * unpublished after the page was loaded. The message counts what went
     * in; a second line names how many did not.
     */
    showSuccess() {
        const bulk = this.cartProcessor.getLastCartData()?.bulk;
        const failed = bulk ? Math.min(bulk.failed, this.items.length) : 0;
        const added = this.items.length - failed;

        const success: HTMLElement = this.container.querySelector(MultiAdd2CartDialogContent.SUCCESS_SELECTOR);
        if (success && added > 0) {
            success.textContent = (success.getAttribute('data-template') || '').replace('{count}', String(added));
            success.hidden = false;
        }

        const partial: HTMLElement = this.container.querySelector(MultiAdd2CartDialogContent.PARTIAL_SELECTOR);
        if (partial && failed > 0) {
            partial.textContent = (partial.getAttribute('data-template') || '').replace('{count}', String(failed));
            partial.hidden = false;
        }

        this.hide(MultiAdd2CartDialogContent.SELECT_STATE_SELECTOR);
        this.show(MultiAdd2CartDialogContent.DONE_STATE_SELECTOR);

        // The quantities are in the cart now and cannot be changed here any
        // more, so the list of formats only takes up room. What remains is
        // the strip of thumbnails, the message, the cart line and the buttons.
        this.hide(MultiAdd2CartDialogContent.FORMATS_SELECTOR);

        document.dispatchEvent(Polyfill.createNewEvent(Events.selectionAdded, {count: added}));
    }

    /**
     * "Buy now": goes to the cart once no request is on its way any more.
     */
    openCart(e: Event) {
        e.preventDefault();
        const href = this.cartButton.getAttribute('data-href');
        if (!href) {
            return;
        }
        if (this.cartProcessor.isBusy()) {
            this.pendingHref = href;
            return;
        }
        document.location.href = href;
    }

    private show(selector: string) {
        const element: HTMLElement = this.container.querySelector(selector);
        if (element) element.hidden = false;
    }

    private hide(selector: string) {
        const element: HTMLElement = this.container.querySelector(selector);
        if (element) element.hidden = true;
    }
}
